import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DayCalendar } from '@/shared/ui';

import styles from './CalendarToolbar.module.css';
import {
  CALENDAR_DENSITIES,
  CALENDAR_MODES,
  type CalendarDensity,
  type CalendarMode,
  type DateRange,
  periodTitle,
} from './model';

interface Props {
  mode: CalendarMode;
  density: CalendarDensity;
  /** Offer the Compact / Hours choice (desktop Week and Work week). */
  showDensity: boolean;
  date: string;
  range: DateRange;
  today: string;
  /** 0 = Monday ... 6 = Sunday */
  firstDay: number;
  locale: string;
  onMode: (mode: CalendarMode) => void;
  onDensity: (density: CalendarDensity) => void;
  onDate: (date: string) => void;
  onPrevious: () => void;
  onNext: () => void;
  /** Extra controls (the Filters button on phones). */
  children?: React.ReactNode;
}

/** Today, previous / next, the period title with a date picker, and the Day / Week / Work week / Month switch. */
export function CalendarToolbar({
  mode,
  density,
  showDensity,
  date,
  range,
  today,
  firstDay,
  locale,
  onMode,
  onDensity,
  onDate,
  onPrevious,
  onNext,
  children,
}: Props) {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerButton = useRef<HTMLButtonElement>(null);
  const title = periodTitle(mode, date, range, locale);
  const showingToday = today >= range.start && today <= range.end;

  return (
    <div
      className={styles.toolbar}
      role="toolbar"
      aria-label={t('calendar.toolbar')}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && pickerOpen) {
          event.stopPropagation();
          setPickerOpen(false);
          pickerButton.current?.focus();
        }
      }}
    >
      <div className={styles.nav}>
        <button
          type="button"
          className={styles.button}
          disabled={showingToday}
          onClick={() => {
            onDate(today);
          }}
        >
          {t('calendar.today')}
        </button>
        <button
          type="button"
          className={`${styles.button} ${styles.icon}`}
          aria-label={t('calendar.previous')}
          title={t('calendar.previous')}
          onClick={onPrevious}
        >
          ←
        </button>
        <button
          type="button"
          className={`${styles.button} ${styles.icon}`}
          aria-label={t('calendar.next')}
          title={t('calendar.next')}
          onClick={onNext}
        >
          →
        </button>
        <div className={styles.titleWrap}>
          <button
            ref={pickerButton}
            type="button"
            className={styles.titleButton}
            aria-haspopup="dialog"
            aria-expanded={pickerOpen}
            aria-controls="calendar-date-picker"
            title={t('calendar.pickDate')}
            onClick={() => {
              setPickerOpen((open) => !open);
            }}
          >
            <h2 className={styles.title} data-testid="calendar-title" aria-live="polite">
              {title}
            </h2>
            <span aria-hidden>▾</span>
            <span className="visually-hidden">{t('calendar.pickDate')}</span>
          </button>
          {pickerOpen && (
            <div id="calendar-date-picker" className={styles.picker}>
              <DayCalendar
                value={date}
                week={{ first: range.start, last: range.end }}
                weekStartsOn={((firstDay + 1) % 7) as 0 | 1 | 2 | 3 | 4 | 5 | 6}
                autoFocus
                onSelect={(day) => {
                  onDate(day);
                  setPickerOpen(false);
                  pickerButton.current?.focus();
                }}
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.modes} role="group" aria-label={t('calendar.view')}>
        {CALENDAR_MODES.map((option) => (
          <button
            key={option}
            type="button"
            className={`${styles.mode} ${option === mode ? styles.active : ''}`}
            aria-pressed={option === mode}
            data-mode={option}
            onClick={() => {
              onMode(option);
            }}
          >
            {t(`calendar.modes.${option}`)}
          </button>
        ))}
      </div>
      {showDensity && (
        <div className={styles.modes} role="group" aria-label={t('calendar.density.label')}>
          {CALENDAR_DENSITIES.map((option) => (
            <button
              key={option}
              type="button"
              className={`${styles.mode} ${option === density ? styles.active : ''}`}
              aria-pressed={option === density}
              data-density={option}
              onClick={() => {
                onDensity(option);
              }}
            >
              {t(`calendar.density.${option}`)}
            </button>
          ))}
        </div>
      )}
      {children}
    </div>
  );
}
